/**
 * Printora header hide-on-scroll — single clean controller v25.
 *
 * Product desktop (1200px+):
 * - Header stays visible while the two product columns travel.
 * - Header releases when the ACTUAL left/right column bottoms meet/cross near
 *   the viewport bottom, not when the whole product section reaches a guess.
 * - A release latch prevents the header animation changing sticky geometry and
 *   immediately re-locking the header.
 *
 * Mobile/tablet (below 1200px):
 * - Header responds immediately to scroll direction.
 * - The product-column release gate is desktop-only.
 *
 * Elsewhere:
 * - Scroll down -> hide.
 * - Scroll up -> show.
 * - Near page top -> always show.
 */
(function () {
  'use strict';

  if (window.__printoraSiteHeaderScroll) return;
  window.__printoraSiteHeaderScroll = true;
  window.__printoraPkHeaderScroll = true;

  var DIRECTION_THRESHOLD = 6;
  var MOBILE_DIRECTION_THRESHOLD = 1;
  var TOP_SHOW = 24;
  var MOBILE_TOP_SHOW = 0;
  var MOBILE_MAX = 1199;
  var WIDTH_TOLERANCE = 2;
  var PRODUCT_DESKTOP_MIN = 1200;
  var PRODUCT_MEET_TOLERANCE = 36;
  var PRODUCT_RELOCK_TOLERANCE = 96;
  var PRODUCT_VIEWPORT_ALLOWANCE = 44;

  var anchorY = 0;
  var ticking = false;
  var hidden = false;
  var measuredViewportWidth = 0;
  var mobileSpacerMeasured = false;
  var scrollHandler = null;
  var resizeHandler = null;
  var resizeTimer = null;
  var productReleased = false;
  var lastProductBottomDelta = null;

  function headerEl() {
    return document.getElementById('printora-sticky-header');
  }

  function currentY() {
    return Math.max(0, window.pageYOffset || document.documentElement.scrollTop || 0);
  }

  function viewportWidth() {
    return Math.round(window.innerWidth || document.documentElement.clientWidth || 0);
  }

  function viewportHeight() {
    return Math.max(0, Math.round(window.innerHeight || document.documentElement.clientHeight || 0));
  }

  function resetProductGate() {
    productReleased = false;
    lastProductBottomDelta = null;
  }

  function productGate(movingDown, movingUp) {
    var body = document.body;
    if (!body || !body.classList.contains('template-product') || viewportWidth() < PRODUCT_DESKTOP_MIN) {
      resetProductGate();
      return 'normal';
    }

    var product = document.querySelector('.productView.pv2-active, .productView');
    var top = product && product.querySelector('.productView-top');
    var left = product && product.querySelector('.halo-productView-left');
    var right = product && product.querySelector('.halo-productView-right, .productView-details');
    if (!top || !left || !right) return 'normal';

    var viewportH = viewportHeight();
    if (!viewportH) return 'normal';

    var topRect = top.getBoundingClientRect();
    var leftRect = left.getBoundingClientRect();
    var rightRect = right.getBoundingClientRect();

    if (topRect.bottom <= 0) {
      productReleased = true;
      lastProductBottomDelta = null;
      return 'released';
    }

    var bottomDelta = leftRect.bottom - rightRect.bottom;
    var absGap = Math.abs(bottomDelta);
    var lowerBottom = Math.max(leftRect.bottom, rightRect.bottom);
    var crossed = false;

    if (lastProductBottomDelta !== null) {
      crossed =
        (lastProductBottomDelta > 0 && bottomDelta <= 0) ||
        (lastProductBottomDelta < 0 && bottomDelta >= 0);
    }
    lastProductBottomDelta = bottomDelta;

    var nearViewportBottom = lowerBottom <= viewportH + PRODUCT_VIEWPORT_ALLOWANCE;
    var clearlyPastReleaseZone = lowerBottom <= Math.max(180, viewportH * 0.55);

    if (!productReleased && movingDown) {
      if ((nearViewportBottom && (absGap <= PRODUCT_MEET_TOLERANCE || crossed)) || clearlyPastReleaseZone) {
        productReleased = true;
      }
    }

    if (productReleased && movingUp) {
      if (absGap > PRODUCT_RELOCK_TOLERANCE || lowerBottom > viewportH + PRODUCT_RELOCK_TOLERANCE) {
        productReleased = false;
      }
    }

    return productReleased ? 'released' : 'locked';
  }

  function unbindWindowListeners() {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler, false);
      scrollHandler = null;
    }
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler, false);
      resizeHandler = null;
    }
    if (resizeTimer) {
      window.clearTimeout(resizeTimer);
      resizeTimer = null;
    }
    ticking = false;
  }

  function clearHalo(header) {
    var nodes = header.querySelectorAll(
      '.section-header-navigation, .section-header-mobile, .shopify-section, sticky-header, sticky-header-mobile'
    );

    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-show',
        'shopify-section-header-sticky',
        'animate',
        'scrolled-past-header'
      );
    }

    var stickies = header.querySelectorAll('sticky-header, sticky-header-mobile');
    for (var j = 0; j < stickies.length; j++) {
      var el = stickies[j];
      if (el._printoraHaloOff) continue;
      el._printoraHaloOff = true;

      try {
        if (el.onScrollHandler) {
          window.removeEventListener('scroll', el.onScrollHandler, false);
        }
      } catch (e) {}

      el.onScrollHandler = function () {};
      el.onScroll = function () {};
      el.hide = function () {};
      el.reveal = function () {};
      el.reset = function () {};

      try {
        if (typeof el.disconnectedCallback === 'function') {
          el.disconnectedCallback();
        }
      } catch (e2) {}
    }
  }

  function ensureSpacer(header, height) {
    var spacer = document.getElementById('printora-header-spacer');

    if (!spacer) {
      spacer = document.createElement('div');
      spacer.id = 'printora-header-spacer';
      spacer.setAttribute('data-printora-header-spacer', '1');
      spacer.setAttribute('aria-hidden', 'true');
      if (header.parentNode) header.parentNode.insertBefore(spacer, header.nextSibling);
    }

    var docTop = Math.round(spacer.getBoundingClientRect().top + currentY());
    if (docTop < 0 || docTop > height) docTop = 0;

    var next = Math.max(0, height - docTop) + 'px';
    if (spacer.style.getPropertyValue('height') !== next) {
      spacer.style.setProperty('height', next, 'important');
    }

    document.documentElement.style.setProperty('--printora-spacer-height', next);
    return spacer;
  }

  function markReady(header) {
    if (header.classList.contains('printora-header-ready')) return;
    window.requestAnimationFrame(function () {
      header.classList.add('printora-header-ready');
    });
  }

  function rawMeasure(header) {
    var hadHidden = header.classList.contains('is-scroll-hidden');
    if (hadHidden) header.classList.add('is-measuring');

    var height = Math.ceil(header.getBoundingClientRect().height || header.offsetHeight || 0);

    if (hadHidden) header.classList.remove('is-measuring');

    if (height > 0) {
      header.style.setProperty('--printora-header-height', height + 'px');
      document.documentElement.style.setProperty('--printora-header-height', height + 'px');
      document.documentElement.style.setProperty('--sticky-area-height', height + 'px');
      ensureSpacer(header, height);
      markReady(header);
    }

    return height;
  }

  function stableMeasure(header, force) {
    var width = viewportWidth();
    var isMobile = width > 0 && width <= MOBILE_MAX;
    var widthChanged =
      !measuredViewportWidth ||
      Math.abs(width - measuredViewportWidth) > WIDTH_TOLERANCE;

    if (!force && isMobile && mobileSpacerMeasured && !widthChanged) {
      return parseInt(header.style.getPropertyValue('--printora-header-height'), 10) || 0;
    }

    var height = rawMeasure(header);
    if (height > 0) {
      measuredViewportWidth = width;
      mobileSpacerMeasured = isMobile;
    }
    return height;
  }

  function menuIsOpen() {
    var body = document.body;
    return !!(
      body &&
      (body.classList.contains('menu_open') || body.classList.contains('menu-open'))
    );
  }

  function setHidden(header, next) {
    if (menuIsOpen()) next = false;
    next = Boolean(next);
    if (hidden === next) return;

    hidden = next;
    header.classList.toggle('is-scroll-hidden', next);
    header.classList.toggle('pk-header-hidden', next);
    document.documentElement.dataset.printoraHeaderHidden = next ? '1' : '0';

    window.dispatchEvent(
      new CustomEvent('printora:header-visibility', {
        detail: { hidden: next }
      })
    );
  }

  function onScroll(header) {
    var y = currentY();
    var width = viewportWidth();
    var isMobileOrTablet = width > 0 && width <= MOBILE_MAX;
    var topShow = isMobileOrTablet ? MOBILE_TOP_SHOW : TOP_SHOW;

    if (y <= topShow) {
      anchorY = y;
      resetProductGate();
      setHidden(header, false);
      return;
    }

    if (menuIsOpen()) {
      anchorY = y;
      setHidden(header, false);
      return;
    }

    var movingDown = y > anchorY;
    var movingUp = y < anchorY;
    var gate = productGate(movingDown, movingUp);

    if (gate === 'locked') {
      anchorY = y;
      setHidden(header, false);
      return;
    }

    if (gate === 'released' && movingDown && !hidden) {
      anchorY = y;
      setHidden(header, true);
      return;
    }

    var delta = y - anchorY;
    var threshold = isMobileOrTablet ? MOBILE_DIRECTION_THRESHOLD : DIRECTION_THRESHOLD;
    if (Math.abs(delta) < threshold) return;

    setHidden(header, delta > 0);
    anchorY = y;
  }

  function bindWindowListeners(header) {
    unbindWindowListeners();

    scrollHandler = function () {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(function () {
        ticking = false;
        onScroll(header);
      });
    };

    resizeHandler = function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        clearHalo(header);
        stableMeasure(header, false);
        anchorY = currentY();
        resetProductGate();
      }, 150);
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    window.addEventListener('resize', resizeHandler, { passive: true });
  }

  function init() {
    var header = headerEl();
    if (!header) return;

    unbindWindowListeners();
    resetProductGate();
    header.dataset.printoraHeaderScroll = '1';

    var slide = header.querySelector(
      ':scope > [data-printora-header-slide], :scope > .printora-header-slide'
    );
    if (slide) {
      while (slide.firstChild) header.insertBefore(slide.firstChild, slide);
      if (slide.parentNode) slide.parentNode.removeChild(slide);
    }

    header.classList.add('printora-header-fixed');
    header.classList.remove(
      'is-scroll-hidden',
      'pk-header-hidden',
      'is-at-top',
      'is-measuring',
      'printora-header-ready'
    );
    header.style.transform = '';
    header.style.marginBottom = '';
    header.style.transition = '';

    clearHalo(header);
    hidden = false;
    document.documentElement.dataset.printoraHeaderHidden = '0';

    stableMeasure(header, true);
    anchorY = currentY();
    bindWindowListeners(header);

    window.setTimeout(function () {
      clearHalo(header);
      stableMeasure(header, true);
    }, 0);

    window.setTimeout(function () {
      clearHalo(header);
      stableMeasure(header, true);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener(
    'load',
    function () {
      var header = headerEl();
      if (!header) return;
      clearHalo(header);
      stableMeasure(header, true);
      anchorY = currentY();
      resetProductGate();
    },
    { once: true }
  );

  document.addEventListener('shopify:section:load', function () {
    hidden = false;
    measuredViewportWidth = 0;
    mobileSpacerMeasured = false;
    anchorY = currentY();
    resetProductGate();
    init();
  });
})();
